/**
 * Data Centre allowances/deductions resolver (#22728) — self-fetching hook,
 * NOT a `dev_*.ts`/`useDevBackendData` boot-time seed. Mirrors
 * `use_employees.ts`/`use_pay_runs.ts`: uses the real bearer client
 * (`getTrpcClient()`, `lib/trpc.ts`) and the shared `useOAuthEntityContext()`
 * helper (`oauth_entity_context.ts`) — never `firm.list`/`firm.create`/
 * `entity.list`. One call, `payroll.listPayItems({firm_id, entity_id})`
 * (allowlisted `payroll:employer:read`/`payroll:employer:write`, PR #22749),
 * split client-side into Allowances (`category: 'earnings'`) and Deductions
 * (`category: 'deduction'`) by `wagepro_store_data_centre.ts`'s pure
 * adapters — see that module's docblock for the full read-mapping rationale
 * and documented gaps (statutory EPF/ETF/APIT never appear here; that's
 * Payroll Policies, #22726).
 *
 * ── No mock fallback ─────────────────────────────────────────────────────
 *
 * Same stance as every other real, OAuth-backed hook in this app: a
 * `FORBIDDEN` from `payroll.listPayItems` specifically caused by the missing
 * `payroll` add-on entitlement (`isPayrollAddonRequiredError`, `lib/trpc.ts`)
 * resolves an explicit `entitlement_required` state; every OTHER failure
 * resolves the generic `error` state. Neither ever falls back to
 * `MOCK_ALLOWANCES`/`MOCK_DEDUCTIONS` — both are deleted from `mock_data.ts`
 * (this hook was their last production reader).
 *
 * ── Disbursement + loan policies (#22730) ───────────────────────────────────
 *
 * `payroll_settings`/`payment_source_accounts` below compose the shared
 * `usePayrollSettings()`/`usePaymentSourceAccounts()` hooks
 * (`use_payroll_settings.ts`) — the SAME per-entity `entity_payroll_settings`
 * read/write model the Settings screen's Payroll tab uses
 * (`use_settings.ts`). Each is its own independent state machine (own
 * loading/error/entitlement_required states) so a failure loading the
 * account picker never blocks the loan-policy fields from rendering, and
 * vice versa. `MOCK_DISBURSEMENT_ACCOUNT`/`MOCK_LOAN_POLICIES` are deleted
 * from `mock_data.ts` — this hook was their last production reader.
 *
 * ── Write path: create/edit through the drawer ──────────────────────────────
 *
 * `save_allowance` calls the real `payroll.createPayItem` (new allowance) or
 * `payroll.updatePayItem` (existing one, keyed on `AllowanceDraft.id`) —
 * both allowlisted `payroll:employer:write`-scope mutations (PR #22749). Only
 * reachable when the OAuth grant is actually write-capable
 * (`isWriteCapableGrant`, reused from `use_pay_runs.ts` rather than
 * re-implementing the same mode-AND-scopes check a second place) AND the read
 * has resolved `ready` — mirrors `use_pay_runs.ts`'s `start_pay_run`/
 * `sign_off` gate exactly: `undefined` otherwise so the screen never offers a
 * button that would just 403. A successful save triggers a real re-fetch
 * (`retry()`'s own mechanism), never an optimistic local flip.
 */
import * as React from 'react';
import { getTrpcClient, isPayrollAddonRequiredError } from '../lib/trpc.js';
import { useAuth } from '../lib/auth/auth_context.js';
import {
  useOAuthEntityContext,
  type OAuthEntityContext,
} from '../lib/auth/oauth_entity_context.js';
import { isWriteCapableGrant } from './use_pay_runs.js';
import {
  buildCreateDeductionPayItemInput,
  buildCreatePayItemInput,
  buildUpdateDeductionPayItemInput,
  buildUpdatePayItemInput,
  deriveAllowancesFromRecords,
  deriveDeductionsFromRecords,
  type AllowanceDraft,
  type DeductionDraft,
  type RealPayItemRecord,
} from './wagepro_store_data_centre.js';
// Re-exported so call sites (e.g. `allowance_drawer.tsx`/`deduction_drawer.tsx`)
// can import the draft/result shapes from this hook's own module, the same
// single seam they already import `UseDataCentreResult` from, rather than
// reaching into the adapter module directly.
export type {
  AllowanceDraft,
  DeductionDraft,
} from './wagepro_store_data_centre.js';
import {
  usePaymentSourceAccounts,
  usePayrollSettings,
  type UsePaymentSourceAccountsResult,
  type UsePayrollSettingsResult,
} from './use_payroll_settings.js';
import type { Allowance, Deduction } from './types';

/** Input the real fetch needs to identify the entity — same shape every `payroll.*` query takes. */
export interface DataCentreFetchInput {
  firm_id: string;
  entity_id: string;
}

/**
 * The one `payroll.listPayItems` call {@link fetchPayItemRecords} needs,
 * isolated behind an injectable seam — mirrors `use_employees.ts`'s
 * `EmployeeRecordsFetchDeps` / `defaultEmployeeRecordsFetchDeps()` DI
 * pattern.
 */
export interface DataCentreFetchDeps {
  listPayItems: (
    input: DataCentreFetchInput,
  ) => Promise<{ pay_items: RealPayItemRecord[] }>;
}

function defaultDataCentreFetchDeps(): DataCentreFetchDeps {
  const client = getTrpcClient();
  return {
    listPayItems: (input) => client.payroll.listPayItems.query(input),
  };
}

/** The two ways a real `payroll.listPayItems` fetch can fail, mirroring `EmployeesLoadFailure` (`use_employees.ts`). */
export type DataCentreLoadFailure = 'entitlement_required' | 'error';

export type DataCentreFetchResult =
  | { outcome: 'success'; records: RealPayItemRecord[] }
  | { outcome: 'failure'; failure: DataCentreLoadFailure };

/** Classifies a caught `payroll.listPayItems` error, matching `use_pay_runs.ts`'s precise add-on-vs-everything-else split (not the coarser `isForbiddenTrpcError`-only classification `use_employees.ts` uses). */
function classifyFailure(err: unknown): DataCentreLoadFailure {
  return isPayrollAddonRequiredError(err) ? 'entitlement_required' : 'error';
}

/**
 * Resolves the entity's real pay-item catalog, or classifies why it
 * couldn't. Exported (and DI'd via `deps`) so the FORBIDDEN classification is
 * unit-testable against a fake client that throws a real-shaped tRPC error —
 * mirrors `use_employees.ts`'s `fetchEmployeeRecords`.
 */
export async function fetchPayItemRecords(
  input: DataCentreFetchInput,
  deps?: DataCentreFetchDeps,
  isCancelled: () => boolean = () => false,
): Promise<DataCentreFetchResult> {
  try {
    const resolvedDeps = deps ?? defaultDataCentreFetchDeps();
    const result = await resolvedDeps.listPayItems(input);
    return { outcome: 'success', records: result.pay_items };
  } catch (err) {
    if (!isCancelled()) {
      console.error(
        '[wagepro-web] Failed to resolve the real pay-item catalog (Data Centre):',
        err,
      );
    }
    return { outcome: 'failure', failure: classifyFailure(err) };
  }
}

export type DataCentreState =
  | { status: 'loading' }
  /** The OAuth grant has no bound entity — see `oauth_entity_context.ts`. Unreachable in practice: `main.tsx`'s `AuthGate` already gates the whole app on this before this hook ever mounts. Handled defensively anyway, never a crash. */
  | { status: 'no_entity' }
  /** The entity is missing the `payroll` add-on entitlement (#21675). NEVER render `MOCK_ALLOWANCES`/`MOCK_DEDUCTIONS` here. */
  | { status: 'entitlement_required' }
  | { status: 'error' }
  | { status: 'ready'; allowances: Allowance[]; deductions: Deduction[] };

/**
 * Pure derivation, exported separately from the hook below so it is
 * unit-testable without rendering React (mirrors `resolveEmployeesState`'s
 * "pure decision function + thin hook" split). Precedence identical to
 * `resolveEmployeesState`: `no_entity` always wins, then resolved `records`
 * win over a stale `failure`, then `entitlement_required`, then `error`,
 * then `loading`.
 */
export function resolveDataCentreState(input: {
  entityContext: OAuthEntityContext;
  records: RealPayItemRecord[] | undefined;
  failure: DataCentreLoadFailure | undefined;
}): DataCentreState {
  if (input.entityContext.status === 'no_entity') {
    return { status: 'no_entity' };
  }
  if (input.records !== undefined) {
    return {
      status: 'ready',
      allowances: deriveAllowancesFromRecords(input.records),
      deductions: deriveDeductionsFromRecords(input.records),
    };
  }
  if (input.failure === 'entitlement_required') {
    return { status: 'entitlement_required' };
  }
  if (input.failure === 'error') {
    return { status: 'error' };
  }
  return { status: 'loading' };
}

export type SaveAllowanceResult =
  | { outcome: 'success' }
  | { outcome: 'failure'; failure: DataCentreLoadFailure };

/** Identical shape to {@link SaveAllowanceResult} — {@link saveDeduction}'s own named result type so call sites read as their own domain concept. */
export type SaveDeductionResult = SaveAllowanceResult;

/**
 * The two real `payroll.*` write mutations {@link saveAllowance}/
 * {@link saveDeduction} need, isolated behind an injectable seam — mirrors
 * `use_pay_runs.ts`'s `PayRunMutationDeps` pattern. Shared by both allowance
 * and deduction saves: both write to the same `pay_items` table, just a
 * different `category`.
 */
export interface DataCentreMutationDeps {
  createPayItem: (
    input: ReturnType<typeof buildCreatePayItemInput>,
  ) => Promise<{ id: string }>;
  updatePayItem: (
    input: ReturnType<typeof buildUpdatePayItemInput>,
  ) => Promise<{ ok: boolean }>;
  archivePayItem: (input: {
    firm_id: string;
    entity_id: string;
    pay_item_id: string;
  }) => Promise<{ ok: boolean }>;
}

function defaultDataCentreMutationDeps(): DataCentreMutationDeps {
  const client = getTrpcClient();
  return {
    createPayItem: (input) => client.payroll.createPayItem.mutate(input),
    updatePayItem: (input) => client.payroll.updatePayItem.mutate(input),
    archivePayItem: (input) => client.payroll.archivePayItem.mutate(input),
  };
}

/**
 * Calls the real `payroll.createPayItem`/`payroll.updatePayItem`, keyed on
 * whether `draft.id` is present. Never throws — every failure mode resolves
 * `{ outcome: 'failure', failure }` with one actionable `console.error`,
 * mirroring `use_pay_runs.ts`'s `startPayRun`/`approvePayRun` never-throw
 * contract, including resolving `deps` INSIDE the try (a synchronous
 * `getTrpcClient()` throw must not escape as an unhandled rejection).
 */
export async function saveAllowance(
  draft: AllowanceDraft,
  entityInput: DataCentreFetchInput,
  deps?: DataCentreMutationDeps,
): Promise<SaveAllowanceResult> {
  try {
    const resolvedDeps = deps ?? defaultDataCentreMutationDeps();
    if (draft.id === null) {
      await resolvedDeps.createPayItem(
        buildCreatePayItemInput(entityInput, draft),
      );
    } else {
      await resolvedDeps.updatePayItem(
        buildUpdatePayItemInput(entityInput, { ...draft, id: draft.id }),
      );
    }
    return { outcome: 'success' };
  } catch (err) {
    console.error('[wagepro-web] data centre: save allowance failed:', err);
    if (isPayrollAddonRequiredError(err)) {
      return { outcome: 'failure', failure: 'entitlement_required' };
    }
    return { outcome: 'failure', failure: 'error' };
  }
}

/**
 * Calls the real `payroll.createPayItem`/`payroll.updatePayItem` for a
 * `category: 'deduction'` pay item, keyed on whether `draft.id` is present.
 * Mirrors {@link saveAllowance} exactly (never throws, same
 * never-throw/failure-classification contract) — the only difference is
 * which `buildCreate*`/`buildUpdate*` input builder it calls.
 */
export async function saveDeduction(
  draft: DeductionDraft,
  entityInput: DataCentreFetchInput,
  deps?: DataCentreMutationDeps,
): Promise<SaveDeductionResult> {
  try {
    const resolvedDeps = deps ?? defaultDataCentreMutationDeps();
    if (draft.id === null) {
      await resolvedDeps.createPayItem(
        buildCreateDeductionPayItemInput(entityInput, draft),
      );
    } else {
      await resolvedDeps.updatePayItem(
        buildUpdateDeductionPayItemInput(entityInput, {
          ...draft,
          id: draft.id,
        }),
      );
    }
    return { outcome: 'success' };
  } catch (err) {
    console.error('[wagepro-web] data centre: save deduction failed:', err);
    if (isPayrollAddonRequiredError(err)) {
      return { outcome: 'failure', failure: 'entitlement_required' };
    }
    return { outcome: 'failure', failure: 'error' };
  }
}

export type ArchivePayItemResult =
  | { outcome: 'success' }
  | { outcome: 'failure'; failure: DataCentreLoadFailure };

/**
 * Calls the real `payroll.archivePayItem` mutation (soft-delete).
 * Mirrors {@link saveAllowance}/{@link saveDeduction} — never throws, same
 * never-throw/failure-classification contract.
 */
export async function archivePayItem(
  payItemId: string,
  entityInput: DataCentreFetchInput,
  deps?: DataCentreMutationDeps,
): Promise<ArchivePayItemResult> {
  try {
    const resolvedDeps = deps ?? defaultDataCentreMutationDeps();
    await resolvedDeps.archivePayItem({
      firm_id: entityInput.firm_id,
      entity_id: entityInput.entity_id,
      pay_item_id: payItemId,
    });
    return { outcome: 'success' };
  } catch (err) {
    console.error('[wagepro-web] data centre: archive pay item failed:', err);
    if (isPayrollAddonRequiredError(err)) {
      return { outcome: 'failure', failure: 'entitlement_required' };
    }
    return { outcome: 'failure', failure: 'error' };
  }
}

export interface UseDataCentreResult {
  state: DataCentreState;
  /** Convenience projection: `[]` for every non-`ready` state. */
  allowances: Allowance[];
  /** Convenience projection: `[]` for every non-`ready` state. */
  deductions: Deduction[];
  /** Still mock — see module docblock's "Still out of scope" section. */
  /**
   * Real, OAuth-backed (#22730) — the entity's disbursement/loan config
   * (`payroll.getPayrollSettings`/`updatePayrollSettings`) and its own
   * loading/error/entitlement_required state machine, independent of the
   * allowances/deductions read above. `data_centre_bank_section.tsx`/
   * `data_centre_loan_section.tsx` render off this directly.
   */
  payroll_settings: UsePayrollSettingsResult;
  /**
   * Real, OAuth-backed (#22730) — the disbursement-account picker's own
   * narrow bank/cash-asset list (`payroll.listPaymentSourceAccounts`, #22324
   * C5), independent of `payroll_settings` above.
   */
  payment_source_accounts: UsePaymentSourceAccountsResult;
  is_loading: boolean;
  /** Re-runs the fetch from scratch — wired to the `error`/`entitlement_required` states' "try again" action. */
  retry: () => void;
  /**
   * Saves an allowance through the real backend. `undefined` when the write
   * path isn't reachable — either the read hasn't resolved `ready` yet, or
   * the OAuth grant isn't write-capable (`!isWriteCapableGrant(grant)`) — so
   * the screen hides the add/edit affordances entirely rather than offering
   * a button that would 403.
   */
  save_allowance:
    | ((draft: AllowanceDraft) => Promise<SaveAllowanceResult>)
    | undefined;
  /**
   * Saves a deduction through the real backend. Mirrors `save_allowance`
   * exactly — same write-path gate (`undefined` unless the read is `ready`
   * AND the OAuth grant is write-capable).
   */
  save_deduction:
    | ((draft: DeductionDraft) => Promise<SaveDeductionResult>)
    | undefined;
  /**
   * Archives a pay item (allowance or deduction) through the real backend.
   * `undefined` when the write path isn't reachable — same gate as
   * `save_allowance`/`save_deduction`. A successful archive triggers a
   * re-fetch so the archived item disappears from the active list.
   */
  archive_pay_item:
    | ((payItemId: string) => Promise<ArchivePayItemResult>)
    | undefined;
}

/** Payroll reference data: allowances, deductions, tax, bank and loan rules. */
export function useDataCentre(): UseDataCentreResult {
  const entityContext = useOAuthEntityContext();
  const { grant } = useAuth();
  const payroll_settings = usePayrollSettings();
  const payment_source_accounts = usePaymentSourceAccounts();
  const [records, setRecords] = React.useState<RealPayItemRecord[] | undefined>(
    undefined,
  );
  const [failure, setFailure] = React.useState<
    DataCentreLoadFailure | undefined
  >(undefined);
  const [reloadToken, setReloadToken] = React.useState(0);

  React.useEffect(() => {
    if (entityContext.status !== 'ready') return;
    // Captured as its own consts so the nested async closure below
    // references definitely-string values rather than relying on narrowing
    // of `entityContext` surviving across the closure boundary — same
    // pattern `use_employees.ts`/`use_pay_runs.ts` use.
    const firmId: string = entityContext.firm_id;
    const entityId: string = entityContext.entity_id;
    let cancelled = false;
    setRecords(undefined);
    setFailure(undefined);

    async function load(): Promise<void> {
      const result = await fetchPayItemRecords(
        { firm_id: firmId, entity_id: entityId },
        undefined,
        () => cancelled,
      );
      if (cancelled) return;
      if (result.outcome === 'success') {
        setRecords(result.records);
      } else {
        setFailure(result.failure);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [entityContext, reloadToken]);

  const state = React.useMemo(
    () => resolveDataCentreState({ entityContext, records, failure }),
    [entityContext, records, failure],
  );

  const readyEntity =
    state.status === 'ready' && entityContext.status === 'ready'
      ? entityContext
      : null;
  const canWrite = readyEntity !== null && isWriteCapableGrant(grant);

  const save_allowance = canWrite
    ? async (draft: AllowanceDraft): Promise<SaveAllowanceResult> => {
        const result = await saveAllowance(draft, {
          firm_id: readyEntity.firm_id,
          entity_id: readyEntity.entity_id,
        });
        if (result.outcome === 'success') {
          setReloadToken((token) => token + 1);
        }
        return result;
      }
    : undefined;

  const save_deduction = canWrite
    ? async (draft: DeductionDraft): Promise<SaveDeductionResult> => {
        const result = await saveDeduction(draft, {
          firm_id: readyEntity.firm_id,
          entity_id: readyEntity.entity_id,
        });
        if (result.outcome === 'success') {
          setReloadToken((token) => token + 1);
        }
        return result;
      }
    : undefined;

  const archive_pay_item = canWrite
    ? async (payItemId: string): Promise<ArchivePayItemResult> => {
        const result = await archivePayItem(payItemId, {
          firm_id: readyEntity.firm_id,
          entity_id: readyEntity.entity_id,
        });
        if (result.outcome === 'success') {
          setReloadToken((token) => token + 1);
        }
        return result;
      }
    : undefined;

  return {
    state,
    allowances: state.status === 'ready' ? state.allowances : [],
    deductions: state.status === 'ready' ? state.deductions : [],
    payroll_settings,
    payment_source_accounts,
    is_loading: state.status === 'loading',
    retry: () => {
      setReloadToken((token) => token + 1);
    },
    save_allowance,
    save_deduction,
    archive_pay_item,
  };
}
